import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { CatalogService } from '@proxy/catalogs/catalog.service';
import { WineDto } from '@proxy/wines/models';
import { ShoppingCartService } from '@proxy/shopping-carts';
import { MetroToastService } from '../../shared/services/metro-toast.service';
import { StorageService } from '../../shared/services/storage.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-assisted-wine-details',
  templateUrl: './assisted-wine-details.component.html',
  standalone: false
})
export class AssistedWineDetailsComponent implements OnInit {
  @Input() wineId!: number;
  @Input() userId!: string;
  @Input() sommelierSlug!: string;
  @Output() closed = new EventEmitter<void>();
  @Output() itemAdded = new EventEmitter<void>();

  wine!: WineDto;
  loading = true;
  adding = false;
  quantity = 1;
  imageUrl: string | null = null;

  constructor(
    private catalogService: CatalogService,
    private shoppingCartService: ShoppingCartService,
    private toasterService: MetroToastService,
    private storageService: StorageService,
    private localizationService: LocalizationService
  ) {}

  ngOnInit(): void {
    this.loadWineDetails();
  }

  loadWineDetails(): void {
    this.loading = true;
    this.catalogService.getWine(this.wineId, this.sommelierSlug)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (res) => {
          this.wine = res;
          this.loadThumbnail();
        },
        error: () => this.toasterService.error('::ErrorLoadingDetails')
      });
  }

  loadThumbnail(): void {
    if (this.wine.fileAttachmentId) {
      this.storageService.getImage(this.wine.fileAttachmentId, 400, 400).subscribe(blob => {
        this.imageUrl = URL.createObjectURL(blob);
      });
    }
  }

  increaseQuantity(): void {
    const max = this.wine.maxOrderQuantity
      ? Math.min(this.wine.stock, this.wine.maxOrderQuantity)
      : Math.min(this.wine.stock, 24);
    if (this.quantity < max) {
      this.quantity++;
    }
  }

  onQuantityChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    const max = this.wine.maxOrderQuantity
      ? Math.min(this.wine.stock, this.wine.maxOrderQuantity)
      : Math.min(this.wine.stock, 24);
    this.quantity = Math.max(1, Math.min(max, value));
  }

  decreaseQuantity(): void {
    if (this.quantity > 1) {
      this.quantity--;
    }
  }

  addToCart(): void {
    if (this.adding) return;
    this.adding = true;
    this.shoppingCartService.addItem(this.userId, this.wine.id, this.quantity).subscribe({
      next: () => {
        this.adding = false;
        this.toasterService.success('::ItemAdded');
        this.itemAdded.emit();
        this.closed.emit();
      },
      error: () => {
        this.adding = false;
        this.toasterService.error('::ErrorAddingItem');
      }
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat(this.localizationService.currentLang || 'es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  }

  getCountryCode(country: string | undefined): string {
    if (!country) return '';
    const normalized = country.trim().toLowerCase();
    const map: Record<string, string> = {
      argentina: 'ar',
      españa: 'es',
      spain: 'es',
      italia: 'it',
      italy: 'it',
      francia: 'fr',
      france: 'fr',
      australia: 'au',
      hungría: 'hu',
      hungary: 'hu'
    };
    return map[normalized] ?? '';
  }
}
